import React, { useCallback, useEffect, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import Layout from "components/Layout"

import { Formik, Form } from "formik"
// import * as yup from "yup"
import { Img, Fieldset, Modal } from "components"

import useInfiniteScroll from "hooks/useInfiniteScroll"

import { toast } from "react-hot-toast"

import { removeFile } from "lib/firebase"

const FILES = gql`
  query Files(
    $filter: FileFilterInput
    $page: Int
    $size: Int
    $sort: FileSortInput
  ) {
    files(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      url
      name
      tags
    }
  }
`

const CREATE_FILES = gql`
  mutation CreateFiles($input: [FileInput]!) {
    createFiles(input: $input) {
      id
      url
      name
      tags
    }
  }
`

const UPDATE_FILE = gql`
  mutation UpdateFile($input: FileInput!) {
    updateFile(input: $input) {
      id
      url
      name
      tags
    }
  }
`

const DELETE_FILE = gql`
  mutation DeleteFile($id: String!) {
    deleteFile(id: $id) {
      id
      url
      name
      tags
    }
  }
`

const size = 20

const MediaPage = () => {
  const [createFiles] = useMutation(CREATE_FILES)
  const [updateFile] = useMutation(UPDATE_FILE)
  const [deleteFile] = useMutation(DELETE_FILE)
  const { data, loading, error, fetchMore } = useQuery(FILES, {
    variables: {
      page: 0,
      size,
      sort: { timestamp: "DESC" },
    },
  })

  const handleCreate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await createFiles({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("File created!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await updateFile({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("File updated!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async ({ name, id }) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      const loadingId = toast.loading("Saving...")
      try {
        const user = await deleteFile({ variables: { id } })
        await removeFile(`/${name}`)
        toast.dismiss(loadingId)
        toast.success("File deleted!")
        return user
      } catch (error) {
        toast.dismiss(loadingId)
        toast.error("Could not save.")
        throw error
      }
    }
  }

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      if (search?.trim() === "") {
        fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult, queryVariables },
          ) => {
            return {
              files: [...fetchMoreResult.files],
            }
          },
        })

        return
      }

      fetchMore({
        variables: { filter: { search } },
        updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
          return {
            files: [
              // ...previousResult.matches,
              ...fetchMoreResult.files,
            ],
          }
        },
      })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return

      fetchMore({
        variables: { page },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return Object.assign({}, previousResult, {
            files: [...previousResult.files, ...fetchMoreResult.files],
          })
        },
      })
    },
    [fetchMore],
  )

  const [selectedFile, setSelectedFile] = useState(null)
  const [isModalOpen, setModalOpen] = useState(false)

  const openModal = useCallback(async () => {
    setModalOpen(true)
  }, [setModalOpen])

  const closeModal = useCallback(async () => {
    setModalOpen(false)
    setSelectedFile(null)
  }, [setModalOpen])

  const handleSelect = (file) => {
    setSelectedFile(file)
    // openModal()
  }

  return (
    <Layout title="Media | GTR" isLoading={loading}>
      <BoxHeader
        title="Media"
        // onExport={exportPassengers}
        onSearch={handleSearch}
        onClick={() => openModal()}
      />

      <MediaList
        loading={loading}
        data={data?.files}
        size={size}
        onLoadMore={handleLoadMore}
        onSelect={handleSelect}
      />

      <MediaDetails
        data={selectedFile}
        isOpen={selectedFile !== null}
        onClose={() => setSelectedFile(null)}
        onSubmit={handleUpdate}
        onDelete={handleDelete}
      />

      <Modal title="Media" isOpen={isModalOpen} onClose={closeModal}>
        <div className="max-w-screen-lg mx-auto">
          <MediaForm data={data} onClose={closeModal} onCreate={handleCreate} />
        </div>
      </Modal>
    </Layout>
  )
}

export default MediaPage

const BoxHeader = ({ title, onSearch, onClick }) => {
  const [query, setQuery] = useState("")

  // useEffect(() => {
  //   onSearch && onSearch(query)
  // }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">{title}</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex justify-end items-center pl-8">
          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Upload
          </button>
        </div>
      </div>
    </header>
  )
}

const MediaList = ({ loading, data, onSelect, onLoadMore }) => {
  const hasMore = loading ? false : data?.length >= size
  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div className="relative -mt-16 z-30">
        <ul className="grid gap-8 grid-cols-4">
          {data?.map((file, index) => (
            <li key={index} onClick={() => onSelect(file)}>
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-indigo-300">
                <Img aspectRatio="16/9" src={file.url} alt="" />
              </div>
            </li>
          ))}

          {hasMore && (
            <div ref={loaderRef} className="text-center col-span-full">
              Loading…
            </div>
          )}
        </ul>
      </div>
    </main>
  )
}

//TODO: Refactor
const MediaDetails = ({ isOpen, data, onClose, onSubmit, onDelete }) => {
  const { data: _tags, refetch } = useQuery(gql`
    query Tags {
      tags
    }
  `)

  const tags = _tags?.tags

  const formikProps = {
    initialValues: {
      ...data,
      tags: data?.tags?.map((tag) => ({
        label: tag,
        value: tag,
      })),
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: async (values) => {
      try {
        await onSubmit({ ...values, tags: values.tags.map((_) => _.value) })
        await refetch()
        onClose()
      } catch (e) {
        console.error("UPDATE TRIP INFROMATION ERROR", e)
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    },
  }

  const handleDelete = async (file) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      try {
        await onDelete(file)
        onClose()
      } catch (error) {
        console.error(error)
      }
    }
  }

  return (
    <Modal title="Media" isOpen={isOpen} onClose={onClose}>
      <Formik {...formikProps}>
        {({ values, errors }) => (
          <Form>
            <div className="flex justify-between">
              <div className="w-7/12 pr-4">
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                  <img
                    className="w-full h-full object-cover"
                    src={data?.url}
                    alt=""
                  />
                </div>
              </div>

              <div className="w-5/12 pl-4">
                {/* <div className="px-2 pb-3">
                    <p className='block text-sm font-medium text-gray-700"'>
                      {values.id}
                    </p>
                  </div>
                  <div className="px-2 py-3 mt-2">
                    <p className='block text-sm font-medium text-gray-700"'>
                      {values.name}
                    </p>
                  </div> */}

                <div className="">
                  <Fieldset
                    name="tags"
                    label="Tags"
                    component="select"
                    options={tags?.map((tag) => ({ label: tag, value: tag }))}
                    // cacheOptions
                    isMulti
                    isCreatable
                    // isLoading={loading}
                    // onInputChange={handleSearch}
                    // getOptionValue={(opt) => opt.key}
                    // mapping={{ label: "displayName" }}
                    // placeholder="Select trip leaders"
                  />
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    className="bg-red-500 text-gray-200 rounded hover:bg-red-400 px-6 py-2 focus:outline-none mx-1"
                    onClick={() => handleDelete(values)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
            {/* <Fieldset component="dropzone" name="files" /> */}
            <ModalNavigation {...{ onClose }} />
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const MediaForm = ({ data, onClose, onCreate, onUpdate, onDelete }) => {
  const initialValues = {
    files: [],
  }

  const onSubmit = useCallback(async (values) => {
    try {
      const { files } = values

      if (files && files.length > 0) {
        onCreate(files)
      }

      onClose()
    } catch (e) {
      console.error("UPDATE TRIP INFROMATION ERROR", e)
      // const errors = e.graphQLErrors.map(v => v)
      // setFieldError("server", errors)
    }
  })

  const formikProps = {
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit,
  }

  return (
    <Formik {...formikProps}>
      {() => (
        <Form>
          <Fieldset component="dropzone" name="files" />
          <ModalNavigation {...{ onClose }} />
        </Form>
      )}
    </Formik>
  )
}

const ModalNavigation = ({ onClose }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Submit
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}
